// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import AppBar from '@material-ui/core/AppBar';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import { Flex, Box } from 'react-system';
import * as icons from '../../icons';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale.js';
import {
  useForceIOSToolbar,
  useDisableIOSBodyScroll,
} from '../../hooks/ios.js';
import { useAnalytics } from '../../controls/analytics';
import { DevelopmentEnquiry } from './DevelopmentEnquiry';
import { useSubmitRegistration } from './useSubmitRegistration';
import type { DevelopmentActionBar_development$key } from './__generated__/DevelopmentActionBar_development.graphql.js';

type Props = {|
  development: DevelopmentActionBar_development$key,
|};

export const DevelopmentActionBar = (props: Props): React.Node => {
  const { t } = useLocale();
  const { track } = useAnalytics();
  const { text } = useTheme();
  const [submitRegistration] = useSubmitRegistration();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  useForceIOSToolbar(!dialogOpen);
  useDisableIOSBodyScroll(dialogOpen);

  const development = useFragment(
    graphql`
      fragment DevelopmentActionBar_development on DevelopmentProduct {
        ...DevelopmentEnquiry_development
        id
        registrationType
        currentUserRegistrationForm {
          id
          completed
        }
      }
    `,
    props.development,
  );

  const registrationComplete =
    development.currentUserRegistrationForm?.completed;
  const registrationId = development.currentUserRegistrationForm?.id;
  let registrationButtonLabel = t('registration');

  if (registrationComplete === true) {
    registrationButtonLabel = t('viewRegistration');
  } else if (registrationId != null) {
    registrationButtonLabel = t('continueRegistration');
  }
  return (
    <>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle disableTypography={true}>
          <Box css={text.h6}>{t('sendEnquiry')}</Box>
          <IconButton
            aria-label="Close"
            onClick={() => setDialogOpen(false)}
            css={{
              position: 'absolute',
              top: 12,
              right: 12,
            }}
          >
            <icons.Close size={24} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DevelopmentEnquiry
            development={development}
            sentActions={
              <Box pt={2}>
                <Button color="primary" onClick={() => setDialogOpen(false)}>
                  {t('backToListing')}
                </Button>
              </Box>
            }
          />
        </DialogContent>
      </Dialog>
      <AppBar color="inherit" css={{ bottom: 0, top: 'auto' }}>
        <Divider />

        <Flex>
          {development.registrationType === 'normal_enquiry' && (
            <Box width={1} px={2} py={2}>
              <Button
                color="primary"
                onClick={() => {
                  track('RA.Web.Development.GetBrochure.Click', {
                    developmentId: development.id,
                  });
                  setDialogOpen(true);
                }}
              >
                {t('requestMoreInfo')}
              </Button>
            </Box>
          )}
          {development.registrationType === 'complete_registration' && (
            <Box width={1} px={2} py={2}>
              <Button
                color="primary"
                onClick={() => {
                  track('RA.Web.Development.Register.Click', {
                    developmentId: development.id,
                  });
                  submitRegistration({
                    id: registrationId,
                    developmentProductId: development.id,
                  });
                }}
              >
                {registrationButtonLabel}
              </Button>
            </Box>
          )}
        </Flex>
      </AppBar>
    </>
  );
};
